import proj4 from 'proj4';
import mapboxgl from "mapbox-gl";
import {getLetterFromIrishGridRef, getLetterOSGB} from "../grid-ref/osgrid";
import {MeasurementPref} from "./MeasurementPref";
import {formatDms} from "./dmsFormat";
import emitter from "../bus";
import {
  gridRefPersistPlan,
  GridRefToken,
  readStoredGridRefToken,
  resolveGridRefToken,
  writeStoredGridRefToken,
} from "./gridRefStorage";
import { i18n } from "../../i18n";

/** Emitted (with the new token) whenever the coordinate system changes on this page. */
export const GRID_REF_CHANGED_EVENT = 'grid-ref:changed';

export enum GridRef {
  GbGrid,
  IeGrid,
  NzGrid,
  LatLon,
  LatLonDms
}

export class GridRefUtils {
  gridRef: GridRef;

  constructor(gridRef: GridRef) {
    this.gridRef = gridRef
  }

  toPrettyString(): String {
    switch (this.gridRef) {
      case GridRef.GbGrid: return "Great Britain Grid Reference (EPSG 27700)";
      case GridRef.IeGrid: return "Irish Grid Reference (EPSG 29902)";
      case GridRef.NzGrid: return "New Zealand Grid Reference (EPSG 27200)"
      case GridRef.LatLon: return "World Geodetic System (Longitude, Latitude) (EPSG 4326)"
      case GridRef.LatLonDms: return "Latitude/Longitude (DMS)"
    }
  }

  toString(): String {
    switch (this.gridRef) {
      case GridRef.GbGrid: return "GbGrid";
      case GridRef.IeGrid: return "IeGrid";
      case GridRef.NzGrid: return "NzGrid";
      case GridRef.LatLon: return "LatLon";
      case GridRef.LatLonDms: return "LatLonDms";
    }
  }

  static fromString(gridRef: String): GridRefUtils {
    switch (gridRef) {
      case "GbGrid": return new GridRefUtils(GridRef.GbGrid);
      case "IeGrid": return new GridRefUtils(GridRef.IeGrid);
      case "NzGrid": return new GridRefUtils(GridRef.NzGrid);
      case "LatLon": return new GridRefUtils(GridRef.LatLon);
      case "LatLonDms": return new GridRefUtils(GridRef.LatLonDms);
      default: return new GridRefUtils(GridRef.LatLon);
    }
  }

  static toProjection(gridRef: GridRef): String {
    switch (gridRef) {
      case GridRef.GbGrid: return "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs";
      case GridRef.IeGrid: return "+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 +ellps=mod_airy +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs";
      case GridRef.NzGrid: return "+proj=nzmg +lat_0=-41 +lon_0=173 +x_0=2510000 +y_0=6023150 +ellps=intl +towgs84=59.47,-5.04,187.44,0.47,-0.1,1.024,-4.5993 +units=m +no_defs";
      case GridRef.LatLon:
      case GridRef.LatLonDms:
        return "+proj=longlat +datum=WGS84 +no_defs";
    }
  }

  /**
   * Persist a new coordinate system. Always remembered on this device; when signed in it is also
   * written to window.user (so preference() reflects it immediately) and pushed to
   * User.gridReference, which is the value the iOS and Android apps read. Emits
   * GRID_REF_CHANGED_EVENT so open UI (the map information bar) re-formats.
   */
  static async setPreference(preference: GridRef): Promise<void> {
    const token = new GridRefUtils(preference).toString() as GridRefToken;
    const plan = gridRefPersistPlan(!!window.user);

    writeStoredGridRefToken(typeof localStorage !== 'undefined' ? localStorage : null, token);
    if (plan.updateWindowUser) {
      // @ts-ignore window.user is the server-serialised user
      window.user.grid_reference = token;
    }
    emitter.emit(GRID_REF_CHANGED_EVENT, token);

    if (plan.pushToServer) {
      try {
        const [{ default: graphClient }, { UpdateUser }] = await Promise.all([
          import('../graphql/graph_client'),
          import('../graphql/user-graph'),
        ]);
        await graphClient.request(UpdateUser, { user: { gridReference: token } });
      } catch (err) {
        console.warn('[GridRefPreference] UpdateUser(gridReference) failed', err);
      }
    }
  }

  static preference(): GridRefUtils {
    const token = resolveGridRefToken(
      // @ts-ignore window.user is the server-serialised user
      window.user?.grid_reference,
      readStoredGridRefToken(typeof localStorage !== 'undefined' ? localStorage : null),
      window.gridRef,
    );
    return GridRefUtils.fromString(token);
  }

  convert(lngLat: mapboxgl.LngLat): String {
    let currentProjection = GridRefUtils.toProjection(this.gridRef)
    // @ts-ignore
    let converted =  proj4(GridRefUtils.toProjection(GridRef.LatLon), currentProjection, [lngLat.lng, lngLat.lat]);
    let returnString = `${converted[0].toPrecision(6)}, ${converted[1].toPrecision(6)}`
    switch (this.gridRef) {
      case GridRef.GbGrid:
        return getLetterOSGB(converted[0], converted[1])
      case GridRef.IeGrid:
        return getLetterFromIrishGridRef(converted[0], converted[1])
      case GridRef.NzGrid:
        return returnString
      case GridRef.LatLon:
        return returnString
      case GridRef.LatLonDms:
        // Same WGS84 point as LatLon, rendered as degrees/minutes/seconds.
        return formatDms(lngLat.lat, lngLat.lng)
    }
  }

  formatFeedback(): String {
    switch (this.gridRef) {
      case GridRef.GbGrid: return i18n.global.t("utils.grid_ref_preference.format_gb_grid");
      case GridRef.IeGrid: return i18n.global.t("utils.grid_ref_preference.format_ie_grid");
      case GridRef.NzGrid: return i18n.global.t("utils.grid_ref_preference.format_nz_grid");
      case GridRef.LatLon:
      case GridRef.LatLonDms:
        return i18n.global.t("utils.grid_ref_preference.format_lat_lon");
    }
  }

  async convertLetteredGBGridRefToLatLng(gridRef: String): Promise<mapboxgl.LngLat> {
    if (this.gridRef !== GridRef.GbGrid) {
      throw new Error("Grid Ref is not GB Grid")
    }
    let l1 = gridRef.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
    let l2 = gridRef.toUpperCase().charCodeAt(1) - 'A'.charCodeAt(0);
    // shuffle down letters after 'I' since 'I' is not used in grid:
    if (l1 > 7) l1 -= 1;
    if (l2 > 7) l2 -= 1;
    // convert grid letters into 100km-square indexes from false origin (grid square SV):
    const e100km = ((l1 - 2) % 5) * 5 + (l2 % 5);
    const n100km = (19 - Math.floor(l1 / 5) * 5) - Math.floor(l2 / 5);
    // skip grid letters to get numeric (easting/northing) part of ref
    const parts = gridRef.split(' ');
    const easting = parts[1];
    const northing = parts[2];

    let eastingFinalString = `${e100km}${easting}`;
    let northingFinalString = `${Math.round(n100km)}${northing}`;

    while (eastingFinalString.length < 6) {
      eastingFinalString += '0';
    }
    while (northingFinalString.length < 6) {
      northingFinalString += '0';
    }

    const eastingNorthing = {
      x: parseFloat(eastingFinalString),
      y: parseFloat(northingFinalString)
    };

    const convertEastingNorthingToLatLong = proj4(
      // @ts-ignore
      GridRefUtils.toProjection(GridRef.GbGrid),
      GridRefUtils.toProjection(GridRef.LatLon),
      [eastingNorthing.x, eastingNorthing.y]
    );
    return new Promise<mapboxgl.LngLat>((resolve, reject) => {
      resolve(new mapboxgl.LngLat(convertEastingNorthingToLatLong[0], convertEastingNorthingToLatLong[1]))
    })
  }

  convertLetteredIrishGridRefToLonLat(gridRef) {
    if (this.gridRef !== GridRef.IeGrid) {
      throw new Error("Grid Ref is not Irish Grid")
    }
    const letter = gridRef.charAt(0);
    const intArray = GridRefUtils.iEGridLetterToArray(letter);
    const splitGridRef = gridRef.split(" ");

    let easting = intArray[0].toString() + splitGridRef[1].toString();
    let northing = intArray[1].toString() + splitGridRef[2].toString();

    while (easting.length < 6) {
      easting += "0";
    }
    while (northing.length < 6) {
      northing += "0";
    }

    const eastingNorthing = {
      x: parseFloat(easting),
      y: parseFloat(northing)
    };
    const convertEastingNorthingToLatLong = proj4(
      // @ts-ignore
      GridRefUtils.toProjection(GridRef.IeGrid),
      GridRefUtils.toProjection(GridRef.LatLon),
      [eastingNorthing.x, eastingNorthing.y]
    );
    return new Promise<mapboxgl.LngLat>((resolve, reject) => {
      resolve(new mapboxgl.LngLat(convertEastingNorthingToLatLong[0], convertEastingNorthingToLatLong[1]))
    })
  }

  convertNzGridRefToLonLat(gridRef) {
    let split = gridRef.split(" ");
    let easting, northing;

    // Check if it's letter-based format (e.g., "BV21 693 764") or numeric (e.g., "2510000 6023150")
    if (split.length === 3) {
      // Letter-based NZMG format: grid code + easting + northing
      const gridCode = split[0];
      const partialEasting = split[1];
      const partialNorthing = split[2];

      // Extract letter prefix and number suffix from grid code
      const match = gridCode.match(/^([A-Z]+)(\d+)$/i);
      if (!match) {
        throw new Error("Invalid NZ Grid reference format");
      }

      const letters = match[1].toUpperCase();
      const gridNum = match[2];

      // Convert letter prefix to base coordinates
      // NZ grid uses letter codes for 100km squares
      const letterOffsets = GridRefUtils.nzGridLetterToOffset(letters);

      // Combine grid number with partial coordinates
      easting = `${letterOffsets.e}${gridNum}${partialEasting}`;
      northing = `${letterOffsets.n}${gridNum}${partialNorthing}`;
    } else if (split.length === 2) {
      // Numeric format
      easting = split[0];
      northing = split[1];
    } else {
      throw new Error("Invalid NZ Grid reference format");
    }

    while (easting.length < 7) {
      easting += "0";
    }
    while (northing.length < 7) {
      northing += "0";
    }

    const eastingNorthing = {
      x: parseFloat(easting),
      y: parseFloat(northing)
    };
    const convertEastingNorthingToLatLong = proj4(
      // @ts-ignore
      GridRefUtils.toProjection(GridRef.NzGrid),
      GridRefUtils.toProjection(GridRef.LatLon),
      [eastingNorthing.x, eastingNorthing.y]
    );
    return new Promise<mapboxgl.LngLat>((resolve, reject) => {
      resolve(new mapboxgl.LngLat(convertEastingNorthingToLatLong[0], convertEastingNorthingToLatLong[1]))
    })

  }

  static nzGridLetterToOffset(letters: string) {
    // NZ Grid letter-based system maps to 100km grid squares
    // NZMG grid square letter codes
    const gridMap = {
      // North Island grid squares
      "BP": { e: 2100, n: 6100 },
      "BQ": { e: 2200, n: 6100 },
      "BR": { e: 2300, n: 6100 },
      "BS": { e: 2400, n: 6100 },
      "BT": { e: 2500, n: 6100 },
      "BU": { e: 2600, n: 6100 },

      "BJ": { e: 2100, n: 6000 },
      "BK": { e: 2200, n: 6000 },
      "BL": { e: 2300, n: 6000 },
      "BM": { e: 2400, n: 6000 },
      "BN": { e: 2500, n: 6000 },
      "BO": { e: 2600, n: 6000 },

      "BC": { e: 2100, n: 5900 },
      "BD": { e: 2200, n: 5900 },
      "BE": { e: 2300, n: 5900 },
      "BF": { e: 2400, n: 5900 },
      "BG": { e: 2500, n: 5900 },
      "BH": { e: 2600, n: 5900 },

      "BV": { e: 2100, n: 5800 },
      "BW": { e: 2200, n: 5800 },
      "BX": { e: 2300, n: 5800 },
      "BY": { e: 2400, n: 5800 },
      "BZ": { e: 2500, n: 5800 },
      "BA": { e: 2600, n: 5800 },

      // South Island grid squares
      "CP": { e: 2100, n: 5700 },
      "CQ": { e: 2200, n: 5700 },
      "CR": { e: 2300, n: 5700 },
      "CS": { e: 2400, n: 5700 },
      "CT": { e: 2500, n: 5700 },
      "CU": { e: 2600, n: 5700 },

      "CJ": { e: 2100, n: 5600 },
      "CK": { e: 2200, n: 5600 },
      "CL": { e: 2300, n: 5600 },
      "CM": { e: 2400, n: 5600 },
      "CN": { e: 2500, n: 5600 },
      "CO": { e: 2600, n: 5600 },

      "CC": { e: 2100, n: 5500 },
      "CD": { e: 2200, n: 5500 },
      "CE": { e: 2300, n: 5500 },
      "CF": { e: 2400, n: 5500 },
      "CG": { e: 2500, n: 5500 },
      "CH": { e: 2600, n: 5500 },

      "CV": { e: 2100, n: 5400 },
      "CW": { e: 2200, n: 5400 },
      "CX": { e: 2300, n: 5400 },
      "CY": { e: 2400, n: 5400 },
      "CZ": { e: 2500, n: 5400 },
      "CA": { e: 2600, n: 5400 },

      "DP": { e: 2100, n: 5300 },
      "DQ": { e: 2200, n: 5300 },
      "DR": { e: 2300, n: 5300 },
      "DS": { e: 2400, n: 5300 },
      "DT": { e: 2500, n: 5300 },
      "DU": { e: 2600, n: 5300 },

      "DJ": { e: 2100, n: 5200 },
      "DK": { e: 2200, n: 5200 },
      "DL": { e: 2300, n: 5200 },
      "DM": { e: 2400, n: 5200 },
      "DN": { e: 2500, n: 5200 },
      "DO": { e: 2600, n: 5200 },

      "DC": { e: 2100, n: 5100 },
      "DD": { e: 2200, n: 5100 },
      "DE": { e: 2300, n: 5100 },
      "DF": { e: 2400, n: 5100 },
      "DG": { e: 2500, n: 5100 },
      "DH": { e: 2600, n: 5100 },
    };

    if (letters in gridMap) {
      return gridMap[letters];
    } else {
      throw new Error(`Unknown NZ Grid code: ${letters}. Please use the numeric format instead (e.g., 2510000 6023150)`);
    }
  }

  static iEGridLetterToArray(letter) {
    const letterMap = {
      "A": [0, 4],
      "B": [1, 4],
      "C": [2, 4],
      "D": [3, 4],
      "E": [4, 4],
      "F": [0, 3],
      "G": [1, 3],
      "H": [2, 3],
      "J": [3, 3],
      "K": [4, 3],
      "L": [0, 2],
      "M": [1, 2],
      "N": [2, 2],
      "O": [3, 2],
      "P": [4, 2],
      "Q": [0, 1],
      "R": [1, 1],
      "S": [2, 1],
      "T": [3, 1],
      "U": [4, 1],
      "V": [0, 0],
      "W": [1, 0],
      "X": [2, 0],
      "Y": [3, 0],
      "Z": [4, 0],
    };

    if (letter in letterMap) {
      return letterMap[letter.toUpperCase()];
    } else {
      throw new Error("Invalid letter: " + letter);
    }
  }

  static validateGridRef(gridRef: string, gridRefType: GridRef): boolean {
    switch (gridRefType) {
      case GridRef.IeGrid:
        return gridRef.split(' ').length === 3
          && gridRef.split(' ')[0].length === 1
          && !isNaN(parseInt(gridRef.split(' ')[1]))
          && !isNaN(parseInt(gridRef.split(' ')[2]));

      case GridRef.GbGrid:
        return gridRef.split(' ').length === 3
          && gridRef.split(' ')[0].length === 2
          && !isNaN(parseInt(gridRef.split(' ')[1]))
          && !isNaN(parseInt(gridRef.split(' ')[2]));

      // DMS is display-only; typed input is still decimal lat/lon.
      case GridRef.LatLon:
      case GridRef.LatLonDms:
        return gridRef.split(' ').length === 2
          && !isNaN(parseFloat(gridRef.split(' ')[0]))
          && !isNaN(parseFloat(gridRef.split(' ')[1]));

      case GridRef.NzGrid:
        return gridRef.split(' ').length === 2
          && !isNaN(parseInt(gridRef.split(' ')[0]))
          && !isNaN(parseInt(gridRef.split(' ')[1]));

      default:
        throw new Error('Unknown grid reference preference');
    }
  }
}
